import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import * as sourceTypes from '@/consts/sourceTypes';
import useIsDesktopLayout from '@/hooks/layout/useIsDesktopLayout';
import appRouterService from '@/services/route/appRouterService';
import {selectHighlightsNavigation} from '@/state/selectors/highlights';
import {clearHighlightsNavigation} from '@/state/slices/highlightsSlice';
import {setInboxMessageId} from '@/state/slices/inboxSlice';

/**
 * Hook to handle push notification navigation for highlights
 * Finds the highlight with matching tag and triggers fullscreen mode
 * Falls back to inbox message if no highlight found
 */
const useHighlightsPushNotification = (findIndexFn) => {
    const dispatch = useDispatch();
    const isDesktopLayout = useIsDesktopLayout();
    const {targetTag, messageId} = useSelector(selectHighlightsNavigation);

    const [notificationTrigger, setNotificationTrigger] = useState(0);
    const [indices, setIndices] = useState({
        globalIndex: 0,
        carouselIndex: 0,
        slideIndex: 0,
    });

    useEffect(() => {
        if (!targetTag) return;

        const result = findIndexFn(targetTag);

        // Support both simple index (number) and structured result (object)
        const globalIndex = typeof result === 'number' ? result : result.globalIndex;
        const notFound = globalIndex === -1;

        // If no highlight found with the tag, redirect to inbox message details
        if (notFound) {
            if (messageId) {
                if (isDesktopLayout) {
                    dispatch(setInboxMessageId(messageId));
                } else {
                    appRouterService.forwardToNotificationDetailsPage(messageId, sourceTypes.HOMEPAGE);
                }
            }
            dispatch(clearHighlightsNavigation());

            return;
        }

        // Found highlight with tag - trigger fullscreen and set starting indices
        setIndices({
            globalIndex,
            carouselIndex: result.carouselIndex ?? 0,
            slideIndex: result.slideIndex ?? 0,
        });
        setNotificationTrigger((prev) => prev + 1);
        dispatch(clearHighlightsNavigation());
    }, [targetTag, messageId, findIndexFn, isDesktopLayout, dispatch]);

    return {
        shouldOpenFullscreen: notificationTrigger > 0,
        notificationTrigger,
        ...indices,
    };
};

export default useHighlightsPushNotification;
